// Pagina PROMEMORIA - lista + creazione/modifica
import { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { PageContainer } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field, Label } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Badge } from '@/components/ui/Misc';
import { useRemindersStore } from '@/store/remindersStore';
import { useNotesStore } from '@/store/notesStore';
import { toast } from '@/hooks/useToast';
import { confirmDialog } from '@/components/ui/Confirm';
import { useI18n, useWeekdays, useWeekdayPresets } from '@/i18n';
import { cn, formatDateTime } from '@/utils';
import type { Reminder } from '@/types';
import { Bell, Plus, Trash2, Clock, Calendar, BellOff, BellRing, FileText } from 'lucide-react';

export function RemindersPage() {
  const { t } = useI18n();
  const { reminders, load, create, update, remove, toggleEnabled } = useRemindersStore();
  const { notes } = useNotesStore();
  const [editing, setEditing] = useState<Reminder | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  function noteTitle(id: string | null) {
    if (!id) return null;
    const n = notes.find((x) => x.id === id);
    return n?.title ?? t('notes.untitled');
  }

  return (
    <>
      <Header
        title={t('reminders.title')}
        subtitle={t('reminders.subtitle', {
          active: reminders.filter((r) => r.enabled).length,
          total: reminders.length,
        })}
        icon={<div className="w-9 h-9 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center"><Bell size={18} className="text-rose-500" /></div>}
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> {t('reminders.new')}
          </Button>
        }
      />

      <PageContainer>
        {reminders.length === 0 ? (
          <EmptyState
            icon={<Bell size={48} strokeWidth={1.5} />}
            title={t('reminders.empty_title')}
            description={t('reminders.empty_desc')}
            action={<Button onClick={() => setShowCreate(true)}><Plus size={16} /> {t('reminders.new')}</Button>}
          />
        ) : (
          <div className="space-y-2.5">
            {reminders.map((r) => (
              <ReminderCard
                key={r.id}
                reminder={r}
                noteTitle={noteTitle(r.note_id)}
                onClick={() => setEditing(r)}
                onToggle={() => toggleEnabled(r.id)}
                onDelete={() => {
                  confirmDialog({
                    title: t('reminders.delete_confirm_title'),
                    message: t('reminders.delete_confirm_msg'),
                    confirmText: t('common.delete'),
                    danger: true,
                    onConfirm: async () => {
                      await remove(r.id);
                      toast.success(t('reminders.deleted'));
                    },
                  });
                }}
              />
            ))}
          </div>
        )}
      </PageContainer>

      {showCreate && (
        <ReminderEditor
          notes={notes}
          onClose={() => setShowCreate(false)}
          onSave={async (data) => {
            await create(data);
            toast.success(t('reminders.created'));
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <ReminderEditor
          reminder={editing}
          notes={notes}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            await update(editing.id, data);
            toast.success(t('reminders.updated'));
            setEditing(null);
          }}
          onDelete={async () => {
            confirmDialog({
              title: t('reminders.delete_confirm_title'),
              message: t('reminders.delete_confirm_msg'),
              confirmText: t('common.delete'),
              danger: true,
              onConfirm: async () => {
                await remove(editing.id);
                toast.success(t('reminders.deleted'));
                setEditing(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------
// Card
// ------------------------------------------------------------
function ReminderCard({
  reminder,
  noteTitle,
  onClick,
  onToggle,
  onDelete,
}: {
  reminder: Reminder;
  noteTitle: string | null;
  onClick: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const weekdays = useWeekdays();

  const daysLabel = reminder.days_of_week.length === 0
    ? t('reminders.label_oneshot')
    : reminder.days_of_week.length === 7
    ? t('reminders.label_all_days')
    : reminder.days_of_week.length === 5 && [1,2,3,4,5].every(d => reminder.days_of_week.includes(d))
    ? t('reminders.label_weekdays')
    : reminder.days_of_week.length === 2 && [0,6].every(d => reminder.days_of_week.includes(d))
    ? t('reminders.label_weekend')
    : weekdays
        .filter(d => reminder.days_of_week.includes(d.value))
        .map(d => d.short)
        .join(', ');

  return (
    <div
      className={cn(
        'rounded-2xl border p-3.5 transition-all cursor-pointer animate-scale-in',
        reminder.enabled
          ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700/60 shadow-sm hover:shadow-md'
          : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60',
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className={cn(
            'shrink-0 mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center transition-colors',
            reminder.enabled
              ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-500'
              : 'bg-slate-200 dark:bg-slate-700 text-slate-400',
          )}
        >
          {reminder.enabled ? <BellRing size={16} className="animate-bell-ring" /> : <BellOff size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm text-slate-900 dark:text-white line-clamp-1">{reminder.title}</h3>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="shrink-0 p-1 rounded text-slate-300 hover:text-rose-500"
            >
              <Trash2 size={14} />
            </button>
          </div>

          {reminder.body && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{reminder.body}</p>
          )}

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {reminder.time && (
              <Badge className="text-[10px] bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                <Clock size={9} /> {reminder.time}
              </Badge>
            )}
            <Badge className="text-[10px] bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              <Calendar size={9} /> {daysLabel}
            </Badge>
            {noteTitle && (
              <Badge className="text-[10px] bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                <FileText size={9} /> {noteTitle}
              </Badge>
            )}
          </div>

          {reminder.at && !reminder.time && (
            <p className="text-[10px] text-slate-400 mt-1.5">{formatDateTime(reminder.at)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Editor
// ------------------------------------------------------------
function ReminderEditor({
  reminder,
  notes,
  onClose,
  onSave,
  onDelete,
}: {
  reminder?: Reminder;
  notes: { id: string; title: string }[];
  onClose: () => void;
  onSave: (data: Partial<Reminder>) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { t } = useI18n();
  const weekdays = useWeekdays();
  const presets = useWeekdayPresets();
  const [title, setTitle] = useState(reminder?.title ?? '');
  const [body, setBody] = useState(reminder?.body ?? '');
  const [time, setTime] = useState(reminder?.time ?? '09:00');
  const [useTime, setUseTime] = useState(!!reminder?.time || !reminder?.at);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(reminder?.days_of_week ?? [1, 2, 3, 4, 5]);
  const [at, setAt] = useState(reminder?.at ?? null);
  const [noteId, setNoteId] = useState<string | null>(reminder?.note_id ?? null);
  const [enabled, setEnabled] = useState(reminder?.enabled ?? true);
  const [saving, setSaving] = useState(false);

  function toggleDay(day: number) {
    setDaysOfWeek((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  }

  function applyPreset(presetId: string) {
    const preset = presets.find((p) => p.id === presetId);
    if (preset) setDaysOfWeek(preset.days);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        title: title.trim() || t('reminders.title'),
        body,
        time: useTime ? time : null,
        days_of_week: useTime ? daysOfWeek : [],
        at: !useTime ? at : null,
        note_id: noteId,
        enabled,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={reminder ? t('reminders.editor_edit') : t('reminders.editor_new')}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {onDelete && reminder ? (
            <Button variant="danger" size="sm" onClick={onDelete}>
              <Trash2 size={14} /> {t('common.delete')}
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button loading={saving} onClick={handleSave} disabled={!title.trim()}>{t('common.save')}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('reminders.field_title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('reminders.field_title_ph')} autoFocus />
        </Field>

        <Field label={t('reminders.field_body')}>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('reminders.field_body_ph')} rows={2} />
        </Field>

        {/* Tipo promemoria: ricorrente vs one-shot */}
        <div>
          <Label>{t('common.all')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setUseTime(true)}
              className={cn(
                'flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all text-xs',
                useTime ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'border-slate-200 dark:border-slate-700 text-slate-500',
              )}
            >
              <Clock size={16} />
              {t('reminders.type_recurring')}
            </button>
            <button
              type="button"
              onClick={() => setUseTime(false)}
              className={cn(
                'flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all text-xs',
                !useTime ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'border-slate-200 dark:border-slate-700 text-slate-500',
              )}
            >
              <Calendar size={16} />
              {t('reminders.type_oneshot')}
            </button>
          </div>
        </div>

        {useTime ? (
          <>
            <Field label={t('reminders.field_time')}>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>

            <div>
              <Label>{t('reminders.field_days')}</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {presets.map((p) => {
                  const active = p.days.length === daysOfWeek.length && p.days.every((d) => daysOfWeek.includes(d));
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p.id)}
                      className={cn(
                        'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                        active ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {weekdays.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={cn(
                      'py-2 rounded-lg text-xs font-semibold transition-all',
                      daysOfWeek.includes(d.value)
                        ? cn(d.color, 'text-white shadow-sm')
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-400',
                    )}
                  >
                    {d.short}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <Field label={t('reminders.field_at')}>
            <Input
              type="datetime-local"
              value={at ? new Date(at).toISOString().slice(0, 16) : ''}
              onChange={(e) => setAt(e.target.value ? new Date(e.target.value).toISOString() : null)}
            />
          </Field>
        )}

        {/* Collega a una nota */}
        {notes.length > 0 && (
          <Field label={t('reminders.field_note')}>
            <select
              value={noteId ?? ''}
              onChange={(e) => setNoteId(e.target.value || null)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"
            >
              <option value="">{t('reminders.field_note_none')}</option>
              {notes.map((n) => (
                <option key={n.id} value={n.id}>{n.title || t('notes.untitled')}</option>
              ))}
            </select>
          </Field>
        )}

        {/* Toggle attivo */}
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60"
        >
          <div className="flex items-center gap-2">
            {enabled ? <BellRing size={16} className="text-rose-500" /> : <BellOff size={16} className="text-slate-400" />}
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {enabled ? t('reminders.enabled') : t('reminders.disabled')}
            </span>
          </div>
          <div className={cn('w-10 h-5 rounded-full relative transition-colors', enabled ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}>
            <div className={cn('absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform', enabled && 'translate-x-5')} />
          </div>
        </button>
      </div>
    </Modal>
  );
}
